import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useTheme } from 'styled-components/native'

import { ADD_NEW_CLIENT_ERROR_CODE_ENUM } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/errorCodes'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { TextButton } from '../../atoms/textButton/TextButton'
import { InlineErrorBanner } from '../../molecules/inlineErrorBanner/InlineErrorBanner'
import { ToastContent } from '../../ui-models/toast/Toast'
import { tID } from '../../utils/utils'
import {
  AddNewClientContent,
  AddNewClientContentRef,
  AddNewClientFormValues,
} from '../addNewClientContent/AddNewClientContent'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'

const ADD_NEW_CLIENT_SUBMIT_EVENT = 'ADD_NEW_CLIENT_SUBMIT'

export type SubmissionError = {
  errorType: ADD_NEW_CLIENT_ERROR_CODE_ENUM
  clientName: string
  bookingLink?: string
}

type AddNewClientErrorData = {
  error_status?: string
  message?: string
}

export type AddNewClientModalProps = {
  customers?: CustomerInfo[]
  providerId: string
  visible: boolean
  onAddNewClient: (args: {
    first_name: string
    last_name: string
    email?: string
    company: string
    dob: string
    provider_id: string
    program_name: string
    pid?: string
    patient_lyra_id?: string
    is_transitioning_client?: boolean
  }) => { unwrap: () => Promise<unknown> }
  isSubmitting: boolean
  onRequestClose: () => void
  track: ({
    event,
    action,
    details,
    page,
    properties,
  }: {
    event: string
    action?: string
    details?: string | { type: string; title: string }
    page?: string
    properties?: Dict
  }) => void
  careTypeOptions: Array<{ label: string; value: string }>
  isFetchingCareTypes?: boolean
  onCompanyChange?: (company: string) => void
  selectedCompany?: string | null
  setAddedClientFirstName: (name: string | null) => void
  setShowSuccessModal: (show: boolean) => void
  setToastContent?: (content: ToastContent) => void
  submissionError: SubmissionError | null
  setSubmissionError: (error: SubmissionError | null) => void
  initialValues?: Partial<AddNewClientFormValues>
  showEmailField?: boolean
  topContent?: React.ReactNode
  transitionContext?: { lyraId: string; isRegistered: boolean }
  onAddSuccess?: () => void
  trackingProperties?: Dict
}

export const AddNewClientModal: React.FC<AddNewClientModalProps> = ({
  customers,
  providerId,
  visible,
  onAddNewClient,
  isSubmitting,
  onRequestClose,
  track,
  careTypeOptions,
  isFetchingCareTypes,
  onCompanyChange,
  selectedCompany,
  setAddedClientFirstName,
  setShowSuccessModal,
  setToastContent,
  submissionError,
  setSubmissionError,
  initialValues,
  showEmailField = true,
  topContent,
  transitionContext,
  onAddSuccess,
  trackingProperties,
}) => {
  const contentRef = useRef<AddNewClientContentRef>(null)
  const [copied, setCopied] = useState(false)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    }
  }, [])

  const mapErrorToSubmissionData = (
    errorData: AddNewClientErrorData | undefined,
    clientName: string,
  ): SubmissionError | null => {
    if (!errorData?.error_status) return null

    switch (errorData.error_status) {
      case ADD_NEW_CLIENT_ERROR_CODE_ENUM.ALREADY_ON_CASELOAD:
        return { errorType: ADD_NEW_CLIENT_ERROR_CODE_ENUM.ALREADY_ON_CASELOAD, clientName }
      case ADD_NEW_CLIENT_ERROR_CODE_ENUM.ALREADY_REGISTERED:
        return {
          errorType: ADD_NEW_CLIENT_ERROR_CODE_ENUM.ALREADY_REGISTERED,
          clientName,
          bookingLink: errorData.message,
        }
      default:
        return null
    }
  }

  const handleCopyBookingLink = useCallback(
    (bookingLink: string) => {
      navigator.clipboard.writeText(bookingLink)
      setCopied(true)
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false)
      }, 2000)
    },
    [setCopied],
  )

  const handleAnimationEnd = () => {
    setCopied(false)
    contentRef.current?.reset()
  }

  const confirmSelection = async () => {
    const values = await contentRef.current?.submit()
    if (!values) return

    track({ event: ADD_NEW_CLIENT_SUBMIT_EVENT, ...(trackingProperties && { properties: trackingProperties }) })
    setSubmissionError(null)
    setCopied(false)

    try {
      await onAddNewClient({
        first_name: values.firstName,
        last_name: values.lastName,
        // The email key is omitted when the field is hidden (registered legacy transitions)
        ...(showEmailField && { email: values.email }),
        company: values.company,
        dob: values.dateOfBirth,
        provider_id: providerId,
        program_name: values.careType,
        pid: values.pid,
        ...(transitionContext && {
          patient_lyra_id: transitionContext.lyraId,
          is_transitioning_client: true,
        }),
      }).unwrap()

      setAddedClientFirstName(values.firstName)
      onRequestClose()
      if (onAddSuccess) {
        onAddSuccess()
      } else {
        setShowSuccessModal(true)
      }
    } catch (error) {
      const errorData = (error as { response?: { data?: AddNewClientErrorData } })?.response?.data
      const mapped = mapErrorToSubmissionData(errorData, values.firstName)
      if (mapped) {
        setSubmissionError(mapped)
      } else {
        onRequestClose()
        setToastContent?.({
          text: formatMessage({
            defaultMessage: 'Something went wrong. Please try again.',
            description: 'Generic error toast when adding a new client fails with an unknown error',
          }),
          id: 'AddNewClient-genericError',
          toastType: 'error',
        })
      }
    }
  }

  const renderErrorBanner = () => {
    if (!submissionError) return null

    const { errorType, clientName, bookingLink } = submissionError

    switch (errorType) {
      case ADD_NEW_CLIENT_ERROR_CODE_ENUM.ALREADY_ON_CASELOAD:
        return (
          <InlineErrorBanner
            title={
              <FormattedMessage
                defaultMessage='{clientName} is already on your Engage caseload'
                description='Error banner title when adding a client who is already on the provider caseload'
                values={{ clientName }}
              />
            }
            text={
              <FormattedMessage
                defaultMessage='You can search for this client on your client list'
                description='Error banner message when adding a client who is already on the provider caseload'
              />
            }
            testId='AddNewClient-errorBanner'
          />
        )
      case ADD_NEW_CLIENT_ERROR_CODE_ENUM.ALREADY_REGISTERED:
        return (
          <InlineErrorBanner
            title={
              <FormattedMessage
                defaultMessage='{clientName} already has a Lyra account'
                description='Error banner title when adding a client who already has a Lyra account'
                values={{ clientName }}
              />
            }
            text={
              <FormattedMessage
                defaultMessage='To start a new episode of care, please email your Direct Booking Link to {clientName}. Once they schedule a session, they will automatically appear in your Client list. <copyLink>{copyLinkText}</copyLink>'
                description='Error banner message when adding a client who already has a Lyra account, with a link to copy the direct booking URL'
                values={{
                  clientName,
                  copyLinkText: copied
                    ? formatMessage({
                        defaultMessage: 'Copied!',
                        description: 'Text shown after successfully copying the booking link',
                      })
                    : formatMessage({
                        defaultMessage: 'Click here to copy direct booking link',
                        description: 'Link text to copy the direct booking URL',
                      }),
                  copyLink: (chunks) => (
                    <TextButton
                      text={chunks}
                      customTextColor={colors.textError}
                      textStyle={{ fontWeight: 'bold' }}
                      onPress={() => {
                        if (bookingLink) {
                          handleCopyBookingLink(bookingLink)
                        }
                      }}
                      testID={tID('AddNewClient-copyBookingLink')}
                    />
                  ),
                }}
              />
            }
            testId='AddNewClient-errorBanner'
          />
        )
      default:
        return null
    }
  }

  return (
    <ConfirmationModal
      visible={visible}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onConfirmationButtonPress={confirmSelection}
      modalTitle={<FormattedMessage defaultMessage='Add client' description='Title of modal to add a new client' />}
      confirmationButtonText={<FormattedMessage defaultMessage='Add' description='Button to add a new client' />}
      cancelButtonText={<FormattedMessage defaultMessage='Cancel' description='Button to cancel adding a new client' />}
      confirmButtonDisabled={false}
      confirmButtonLoading={isSubmitting}
      testID={tID('AddNewClientModal')}
      modalContents={
        <>
          {topContent}
          {renderErrorBanner()}
          <AddNewClientContent
            ref={contentRef}
            customers={customers ?? []}
            careTypeOptions={careTypeOptions}
            isFetchingCareTypes={isFetchingCareTypes}
            onCompanyChange={onCompanyChange}
            selectedCompany={selectedCompany}
            initialValues={initialValues}
            showEmailField={showEmailField}
            isTransitioning={!!transitionContext}
            isRegistered={transitionContext?.isRegistered}
          />
        </>
      }
    />
  )
}
